// src/components/search/GlobalSearch.js
// Enterprise-grade Global Search with: abort controllers, request deduplication, LRU cache,
// keyboard navigation (↑/↓/Enter/Esc/Cmd+K), recent searches, grouped results, query highlighting,
// retry logic, accessibility (ARIA combobox), error states, and full theme support.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box, Input, InputGroup, InputLeftElement, InputRightElement,
  Icon, IconButton, Spinner, useColorModeValue, List, ListItem,
  Text, HStack, VStack, Badge, Collapse, Flex, Kbd, Tooltip,
  Divider, Button, Alert, AlertIcon
} from '@chakra-ui/react';
import {
  MdSearch, MdClose, MdBuild, MdInventory, MdDashboard, MdHeadsetMic,
  MdHistory, MdTrendingUp, MdErrorOutline, MdRefresh, MdKeyboardReturn,
  MdArrowUpward, MdArrowDownward // <-- Added sorting icons
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { config } from '../../config';
import { getAuthToken } from '../../utils/auth';

// ============================================================================
// CONSTANTS — tuned for enterprise scale
// ============================================================================
const DEBOUNCE_MS = 350;              // Debounce window
const MIN_QUERY_LENGTH = 2;           // Don't search single chars
const REQUEST_TIMEOUT_MS = 8000;      // Abort hung requests
const MAX_RECENT_SEARCHES = 8;        // LRU cap for history
const MAX_CACHE_ENTRIES = 50;         // LRU cap for response cache
const CACHE_TTL_MS = 60_000;          // 60s — results may go stale
const MAX_RETRIES = 2;                // Retry transient failures
const RETRY_BACKOFF_MS = 500;         // Exponential base
const RECENT_SEARCH_KEY = 'flowsense_recent_searches_v1';

// Entity type configuration — single source of truth
// --- FIXED: Updated routes to match src/routes.js exact paths ---
const ENTITY_CONFIG = {
  work_order: { label: 'Work Order',  icon: MdBuild,       color: 'blue',   route: (r) => `/admin/workorders?search=${r.id}` },
  asset:      { label: 'Asset',       icon: MdDashboard,   color: 'green',  route: (r) => `/admin/digital-twin?search=${r.id}` },
  inventory:  { label: 'Inventory',   icon: MdInventory,   color: 'orange', route: (r) => `/admin/inventory?search=${encodeURIComponent(r.title || r.name || r.id)}` },
  helpdesk:   { label: 'Helpdesk',    icon: MdHeadsetMic,  color: 'teal',   route: (r) => `/admin/helpdesk?search=${r.id}` },
};

const DEFAULT_ENTITY = { label: 'Result', icon: MdSearch, color: 'gray', route: () => '/' };

// ============================================================================
// MODULE-LEVEL LRU CACHE — survives component remounts within session
// Map preserves insertion order, perfect for LRU eviction
// ============================================================================
const responseCache = new Map();

const getCached = (key) => {
  const entry = responseCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    responseCache.delete(key);
    return null;
  }
  // Refresh LRU position
  responseCache.delete(key);
  responseCache.set(key, entry);
  return entry.data;
};

const setCached = (key, data) => {
  if (responseCache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = responseCache.keys().next().value;
    responseCache.delete(oldestKey);
  }
  responseCache.set(key, { data, timestamp: Date.now() });
};

// ============================================================================
// LocalStorage helpers — wrapped to survive private browsing / quota errors
// ============================================================================
const loadRecentSearches = () => {
  try {
    const raw = localStorage.getItem(RECENT_SEARCH_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT_SEARCHES) : [];
  } catch {
    return [];
  }
};

const saveRecentSearches = (searches) => {
  try {
    localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(searches.slice(0, MAX_RECENT_SEARCHES)));
  } catch {
    // Quota exceeded or private mode — fail silently, this is non-critical
  }
};

// ============================================================================
// Query highlighter — escapes regex special chars to prevent ReDoS
// ============================================================================
const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const HighlightedText = ({ text, query, ...props }) => {
  if (!text || !query || query.length < MIN_QUERY_LENGTH) {
    return <Text {...props}>{text}</Text>;
  }
  try {
    const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
    const parts = String(text).split(regex);
    return (
      <Text {...props}>
        {parts.map((part, i) =>
          regex.test(part)
            ? <Box as="mark" key={i} bg="yellow.200" color="inherit" px={0.5} borderRadius="sm">{part}</Box>
            : <React.Fragment key={i}>{part}</React.Fragment>
        )}
      </Text>
    );
  } catch {
    return <Text {...props}>{text}</Text>;
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export const GlobalSearch = ({ isMobile, showSearch, setShowSearch }) => {
  // ------- State -------
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState(() => loadRecentSearches());
  
  // --- NEW: Sort State ---
  const [sortOrder, setSortOrder] = useState('asc'); 

  // ------- Refs -------
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0); // Monotonic request ID — discard stale responses

  const navigate = useNavigate();

  // ------- Theme tokens — all dark-mode aware -------
  const searchBg     = useColorModeValue('white', 'navy.900');
  const resultBg     = useColorModeValue('white', 'gray.800');
  const hoverBg      = useColorModeValue('gray.50', 'gray.700');
  const activeBg     = useColorModeValue('blue.50', 'blue.900');
  const borderColor  = useColorModeValue('gray.200', 'gray.600');
  const mutedColor   = useColorModeValue('gray.500', 'gray.400');
  const headingColor = useColorModeValue('gray.700', 'gray.200');
  const iconBoxBg    = useColorModeValue('gray.100', 'gray.700');
  const sectionBg    = useColorModeValue('gray.50', 'gray.900');
  const kbdBg        = useColorModeValue('gray.100', 'gray.700');

  // ============================================================================
  // FETCH WITH RETRY, ABORT, AND TIMEOUT
  // ============================================================================
  const performSearch = useCallback(async (searchTerm, attempt = 0) => {
    // Cancel any in-flight request before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Each request gets a monotonic ID; only the latest is allowed to update state
    const requestId = ++requestIdRef.current;

    // Hard timeout — protects against hung backend
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${config.apiUrl}/search?q=${encodeURIComponent(searchTerm)}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const items = Array.isArray(data?.data) ? data.data : [];

      // Stale response guard — ignore if a newer request has been issued
      if (requestId !== requestIdRef.current) return null;

      // Cache success
      setCached(searchTerm.toLowerCase().trim(), items);
      return items;

    } catch (err) {
      clearTimeout(timeoutId);

      // Aborted requests are not errors — just discard
      if (err.name === 'AbortError') return null;

      // Retry on transient failures
      if (attempt < MAX_RETRIES) {
        const backoff = RETRY_BACKOFF_MS * Math.pow(2, attempt);
        await new Promise((res) => setTimeout(res, backoff));
        return performSearch(searchTerm, attempt + 1);
      }

      // Stale guard before reporting error
      if (requestId !== requestIdRef.current) return null;
      throw err;
    }
  }, []);

  // ============================================================================
  // DEBOUNCED SEARCH EFFECT
  // ============================================================================
  useEffect(() => {
    const trimmed = query.trim();

    // Empty query — close results, but keep dropdown open if focused (shows recents)
    if (!trimmed) {
      setResults([]);
      setError(null);
      setIsSearching(false);
      return;
    }

    // Skip very short queries to save backend cycles
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setError(null);
      setIsSearching(false);
      return;
    }

    // Cache hit — instant results, no spinner, no network
    const cacheKey = trimmed.toLowerCase();
    const cached = getCached(cacheKey);
    if (cached) {
      setResults(cached);
      setError(null);
      setIsSearching(false);
      setIsOpen(true);
      setHighlightedIndex(-1);
      return;
    }

    // Debounce network call
    setIsSearching(true);
    setError(null);
    const debounceTimer = setTimeout(async () => {
      try {
        const items = await performSearch(trimmed);
        if (items === null) return; // Aborted or stale
        setResults(items);
        setIsOpen(true);
        setHighlightedIndex(-1);
      } catch (err) {
        console.error('[GlobalSearch] Search failed:', err);
        setError(err.message || 'Search failed');
        setResults([]);
        setIsOpen(true);
      } finally {
        setIsSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [query, performSearch]);

  // Cleanup any in-flight request on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ============================================================================
  // CLICK OUTSIDE — close dropdown
  // ============================================================================
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============================================================================
  // GLOBAL Cmd+K / Ctrl+K SHORTCUT — enterprise standard
  // ============================================================================
  useEffect(() => {
    if (isMobile) return; // Skip on mobile
    const handleGlobalKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
    };
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [isMobile]);

  // ============================================================================
  // NEW: CLIENT-SIDE SORTING LOGIC BEFORE GROUPING
  // ============================================================================
  const sortedResults = useMemo(() => {
    return [...results].sort((a, b) => {
      const titleA = (a.title || '').toLowerCase();
      const titleB = (b.title || '').toLowerCase();
      if (titleA < titleB) return sortOrder === 'asc' ? -1 : 1;
      if (titleA > titleB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [results, sortOrder]);

  // ============================================================================
  // RESULT GROUPING — group by entity type for cleaner UX (Using sortedResults)
  // ============================================================================
  const groupedResults = useMemo(() => {
    if (!sortedResults.length) return [];
    const groups = {};
    sortedResults.forEach((r) => {
      const type = r.type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(r);
    });
    return Object.entries(groups).map(([type, items]) => ({ type, items }));
  }, [sortedResults]);

  // Flat list with group-aware indexing for keyboard navigation
  const flatResults = useMemo(() => {
    return groupedResults.flatMap((g) => g.items);
  }, [groupedResults]);

  // ============================================================================
  // RECENT SEARCHES — persist to localStorage
  // ============================================================================
  const addToRecentSearches = useCallback((term) => {
    if (!term || term.length < MIN_QUERY_LENGTH) return;
    setRecentSearches((prev) => {
      const filtered = prev.filter((s) => s.toLowerCase() !== term.toLowerCase());
      const next = [term, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      saveRecentSearches(next);
      return next;
    });
  }, []);

  const clearRecentSearches = useCallback(() => {
    setRecentSearches([]);
    saveRecentSearches([]);
  }, []);

  // ============================================================================
  // RESULT NAVIGATION
  // ============================================================================
  const handleResultClick = useCallback((result) => {
    const config = ENTITY_CONFIG[result.type] || DEFAULT_ENTITY;
    addToRecentSearches(query.trim());
    setIsOpen(false);
    setQuery('');
    setHighlightedIndex(-1);
    if (isMobile && setShowSearch) setShowSearch(false);
    navigate(config.route(result));
  }, [query, isMobile, setShowSearch, navigate, addToRecentSearches]);

  // ============================================================================
  // KEYBOARD NAVIGATION — full WAI-ARIA combobox pattern
  // ============================================================================
  const handleKeyDown = useCallback((e) => {
    // Recent search selected via keyboard when no results
    const showingRecents = !query.trim() && recentSearches.length > 0;
    const navigableItems = showingRecents ? recentSearches : flatResults;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          return;
        }
        setHighlightedIndex((i) => Math.min(i + 1, navigableItems.length - 1));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, -1));
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && navigableItems[highlightedIndex] !== undefined) {
          if (showingRecents) {
            setQuery(navigableItems[highlightedIndex]);
          } else {
            handleResultClick(navigableItems[highlightedIndex]);
          }
        }
        break;

      case 'Escape':
        e.preventDefault();
        if (query) {
          setQuery('');
        } else {
          setIsOpen(false);
          inputRef.current?.blur();
          if (isMobile && setShowSearch) setShowSearch(false);
        }
        setHighlightedIndex(-1);
        break;

      case 'Tab':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;

      default:
        break;
    }
  }, [isOpen, highlightedIndex, flatResults, recentSearches, query, handleResultClick, isMobile, setShowSearch]);

  // Auto-scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const item = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
    if (item && typeof item.scrollIntoView === 'function') {
      item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const handleClear = useCallback(() => {
    setQuery('');
    setResults([]);
    setError(null);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  }, []);

  const handleFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleRetry = useCallback(() => {
    if (query.trim().length >= MIN_QUERY_LENGTH) {
      // Force-bust cache for this term and re-trigger by toggling
      responseCache.delete(query.trim().toLowerCase());
      setError(null);
      setQuery((q) => q + ''); // No-op state update to retrigger effect
      // Trigger via setQuery same string won't fire — bump and restore
      const current = query;
      setQuery('');
      setTimeout(() => setQuery(current), 0);
    }
  }, [query]);

  const handleRecentClick = useCallback((term) => {
    setQuery(term);
    inputRef.current?.focus();
  }, []);

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================
  const getEntityConfig = (type) => ENTITY_CONFIG[type] || DEFAULT_ENTITY;

  const showDropdown = isOpen && (
    isSearching ||
    error ||
    flatResults.length > 0 ||
    (query.trim().length >= MIN_QUERY_LENGTH && !isSearching) ||
    (!query.trim() && recentSearches.length > 0)
  );

  // Compute the absolute index of an item across all groups (for keyboard nav)
  let globalIndex = -1;

  // ============================================================================
  // SEARCH CONTENT
  // ============================================================================
  const SearchContent = (
    <Box position="relative" w="full" ref={wrapperRef}>
      <InputGroup
        size="md"
        w={{ base: '100%', lg: '320px' }}
        bg={searchBg}
        borderRadius="lg"
        role="combobox"
        aria-expanded={showDropdown}
        aria-haspopup="listbox"
        aria-owns="global-search-listbox"
      >
        <InputLeftElement pointerEvents="none">
          <Icon as={MdSearch} color={mutedColor} />
        </InputLeftElement>
        <Input
          ref={inputRef}
          placeholder={isMobile ? 'Search…' : 'Search work orders, assets, inventory…'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          borderRadius="lg"
          borderWidth="1px"
          borderColor={borderColor}
          _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
          aria-label="Global search"
          aria-autocomplete="list"
          aria-controls="global-search-listbox"
          aria-activedescendant={highlightedIndex >= 0 ? `search-result-${highlightedIndex}` : undefined}
          autoComplete="off"
          spellCheck={false}
        />
        <InputRightElement width={isMobile ? 'auto' : '5rem'} pr={2}>
          <HStack spacing={1}>
            {isSearching && <Spinner size="sm" color="blue.500" thickness="2px" />}
            {!isSearching && query && (
              <IconButton
                icon={<MdClose />}
                size="xs"
                variant="ghost"
                onClick={handleClear}
                aria-label="Clear search"
              />
            )}
            {!isSearching && !query && !isMobile && (
              <Tooltip label="Press Cmd+K (or Ctrl+K) to focus search" hasArrow placement="bottom">
                <HStack spacing={0.5} pr={1}>
                  <Kbd fontSize="2xs" bg={kbdBg}>⌘</Kbd>
                  <Kbd fontSize="2xs" bg={kbdBg}>K</Kbd>
                </HStack>
              </Tooltip>
            )}
          </HStack>
        </InputRightElement>
      </InputGroup>

      {/* Dropdown */}
      {showDropdown && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={2}
          bg={resultBg}
          boxShadow="2xl"
          borderRadius="xl"
          borderWidth="1px"
          borderColor={borderColor}
          maxH="480px"
          overflowY="auto"
          zIndex={1400}
          id="global-search-listbox"
          role="listbox"
          ref={listRef}
        >
          {/* ERROR STATE */}
          {error && !isSearching && (
            <Box p={4}>
              <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon as={MdErrorOutline} />
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="semibold">Search unavailable</Text>
                  <Text fontSize="xs" color={mutedColor}>{error}</Text>
                </Box>
                <Button
                  size="xs"
                  leftIcon={<MdRefresh />}
                  onClick={handleRetry}
                  ml={2}
                  variant="ghost"
                >
                  Retry
                </Button>
              </Alert>
            </Box>
          )}

          {/* LOADING SKELETON */}
          {isSearching && !error && (
            <VStack spacing={0} align="stretch">
              {[0, 1, 2].map((i) => (
                <HStack key={i} p={3} spacing={3}>
                  <Box w="32px" h="32px" bg={iconBoxBg} borderRadius="md" />
                  <VStack align="stretch" flex={1} spacing={1}>
                    <Box h="12px" bg={iconBoxBg} borderRadius="sm" w="60%" />
                    <Box h="10px" bg={iconBoxBg} borderRadius="sm" w="40%" />
                  </VStack>
                </HStack>
              ))}
            </VStack>
          )}

          {/* RECENT SEARCHES — shown when query is empty */}
          {!query.trim() && !isSearching && !error && recentSearches.length > 0 && (
            <VStack align="stretch" spacing={0}>
              <Flex
                px={3}
                py={2}
                bg={sectionBg}
                justify="space-between"
                align="center"
                borderBottomWidth="1px"
                borderColor={borderColor}
              >
                <HStack spacing={2}>
                  <Icon as={MdHistory} color={mutedColor} boxSize={3.5} />
                  <Text fontSize="xs" fontWeight="700" color={headingColor} letterSpacing="wider">RECENT SEARCHES</Text>
                </HStack>
                <Button size="xs" variant="ghost" onClick={clearRecentSearches} fontSize="2xs">
                  Clear
                </Button>
              </Flex>
              <List spacing={0}>
                {recentSearches.map((term, idx) => {
                  const isHighlighted = highlightedIndex === idx;
                  return (
                    <ListItem
                      key={`recent-${idx}`}
                      data-index={idx}
                      id={`search-result-${idx}`}
                      role="option"
                      aria-selected={isHighlighted}
                      px={3}
                      py={2}
                      cursor="pointer"
                      bg={isHighlighted ? activeBg : 'transparent'}
                      _hover={{ bg: hoverBg }}
                      onClick={() => handleRecentClick(term)}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                    >
                      <HStack spacing={3}>
                        <Icon as={MdHistory} color={mutedColor} boxSize={4} />
                        <Text fontSize="sm" flex={1} noOfLines={1}>{term}</Text>
                      </HStack>
                    </ListItem>
                  );
                })}
              </List>
            </VStack>
          )}

          {/* RESULTS — grouped by entity type */}
          {!isSearching && !error && flatResults.length > 0 && (
            <VStack align="stretch" spacing={0}>
              {/* --- NEW: Sorting Toolbar --- */}
              <Flex
                px={3} py={1.5} bg={searchBg}
                borderBottomWidth="1px" borderColor={borderColor}
                justify="space-between" align="center"
                position="sticky" top={0} zIndex={2}
              >
                 <Text fontSize="xs" fontWeight="bold" color={mutedColor} textTransform="uppercase">
                    Matched Records ({flatResults.length})
                 </Text>
                 <Tooltip label={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`} hasArrow>
                     <Button
                         size="xs"
                         variant="ghost"
                         color={mutedColor}
                         leftIcon={<Icon as={sortOrder === 'asc' ? MdArrowDownward : MdArrowUpward} />}
                         onClick={(e) => {
                             e.stopPropagation();
                             setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                         }}
                     >
                         Sort
                     </Button>
                 </Tooltip>
              </Flex>
              {/* --- END NEW --- */}

              {groupedResults.map((group) => {
                const cfg = getEntityConfig(group.type);
                return (
                  <Box key={group.type}>
                    <Flex
                      px={3}
                      py={2}
                      bg={sectionBg}
                      align="center"
                      borderBottomWidth="1px"
                      borderColor={borderColor}
                      position="sticky"
                      top={0}
                      zIndex={1}
                    >
                      <Icon as={cfg.icon} color={`${cfg.color}.500`} mr={2} boxSize={3.5} />
                      <Text fontSize="xs" fontWeight="700" color={headingColor} letterSpacing="wider" textTransform="uppercase">
                        {cfg.label}
                      </Text>
                      <Badge ml={2} colorScheme={cfg.color} fontSize="2xs">{group.items.length}</Badge>
                    </Flex>
                    <List spacing={0}>
                      {group.items.map((res) => {
                        globalIndex += 1;
                        const idx = globalIndex;
                        const isHighlighted = highlightedIndex === idx;
                        return (
                          <ListItem
                            key={`${res.type}-${res.id || idx}`}
                            data-index={idx}
                            id={`search-result-${idx}`}
                            role="option"
                            aria-selected={isHighlighted}
                            px={3}
                            py={3}
                            cursor="pointer"
                            bg={isHighlighted ? activeBg : 'transparent'}
                            _hover={{ bg: hoverBg }}
                            borderBottomWidth="1px"
                            borderColor={borderColor}
                            onClick={() => handleResultClick(res)}
                            onMouseEnter={() => setHighlightedIndex(idx)}
                          >
                            <HStack spacing={3} align="flex-start">
                              <Box
                                p={2}
                                bg={iconBoxBg}
                                borderRadius="md"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                              >
                                <Icon as={cfg.icon} color={`${cfg.color}.500`} boxSize={4} />
                              </Box>
                              <Box flex={1} minW={0}>
                                <HStack justify="space-between" mb={0.5} spacing={2}>
                                  <Box flex={1} minW={0}>
                                    <HighlightedText
                                      text={res.title}
                                      query={query}
                                      fontSize="sm"
                                      fontWeight="600"
                                      noOfLines={1}
                                      color={headingColor}
                                    />
                                  </Box>
                                  {isHighlighted && (
                                    <Tooltip label="Press Enter to open" hasArrow>
                                      <Box flexShrink={0}>
                                        <Icon as={MdKeyboardReturn} color={mutedColor} boxSize={3.5} />
                                      </Box>
                                    </Tooltip>
                                  )}
                                </HStack>
                                <HighlightedText
                                  text={res.subtitle || res.id || ''}
                                  query={query}
                                  fontSize="xs"
                                  color={mutedColor}
                                  noOfLines={1}
                                />
                              </Box>
                            </HStack>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                );
              })}

              {/* Footer with keyboard hints */}
              {!isMobile && (
                <Flex
                  px={3}
                  py={2}
                  bg={sectionBg}
                  borderTopWidth="1px"
                  borderColor={borderColor}
                  fontSize="2xs"
                  color={mutedColor}
                  justify="space-between"
                  align="center"
                >
                  <HStack spacing={3}>
                    <HStack spacing={1}>
                      <Kbd fontSize="2xs" bg={kbdBg}>↑</Kbd>
                      <Kbd fontSize="2xs" bg={kbdBg}>↓</Kbd>
                      <Text>navigate</Text>
                    </HStack>
                    <HStack spacing={1}>
                      <Kbd fontSize="2xs" bg={kbdBg}>↵</Kbd>
                      <Text>open</Text>
                    </HStack>
                    <HStack spacing={1}>
                      <Kbd fontSize="2xs" bg={kbdBg}>esc</Kbd>
                      <Text>close</Text>
                    </HStack>
                  </HStack>
                  <Text>{flatResults.length} result{flatResults.length !== 1 ? 's' : ''}</Text>
                </Flex>
              )}
            </VStack>
          )}

          {/* EMPTY RESULTS — query entered but nothing found */}
          {!isSearching && !error && query.trim().length >= MIN_QUERY_LENGTH && flatResults.length === 0 && (
            <Box p={6} textAlign="center">
              <Icon as={MdSearch} boxSize={8} color={mutedColor} mb={2} />
              <Text fontSize="sm" fontWeight="semibold" color={headingColor} mb={1}>
                No results for "{query}"
              </Text>
              <Text fontSize="xs" color={mutedColor}>
                Try a different keyword or check spelling
              </Text>
            </Box>
          )}

          {/* MIN-LENGTH HINT */}
          {!isSearching && !error && query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
            <Box p={4} textAlign="center">
              <Text fontSize="xs" color={mutedColor}>
                Type at least {MIN_QUERY_LENGTH} characters to search
              </Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

  // ============================================================================
  // MOBILE WRAPPER
  // ============================================================================
  if (isMobile) {
    return (
      <Collapse in={showSearch} animateOpacity>
        <Box mt={3} position="relative" zIndex={2}>
          {SearchContent}
        </Box>
      </Collapse>
    );
  }

  return SearchContent;
};

export default GlobalSearch;